/**
 * Lite YouTube Embed - Facade Pattern
 * Replaces heavy YouTube iframes with lightweight thumbnails
 * Loads the real iframe only when the user clicks play
 * Avoids loading the YouTube player until it is requested
 *
 * Usage: <lite-youtube videoid="VIDEO_ID" playlabel="Play"></lite-youtube>
 */
(function() {
  'use strict';

  class LiteYouTube extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.videoId = this.getAttribute('videoid');
      if (!this.videoId) return;

      this.playLabel = this.getAttribute('playlabel') || 'Play video';
      this.autoload = this.hasAttribute('autoload');
      this.params = this.getAttribute('params') || '';
      this.posterLoaded = false;

      this.setupDOM();
      this.setupPrivacyAwarePoster();

      if (!this.autoload) {
        this.activationHandler = () => this.requestActivation();
        this.addEventListener('click', this.activationHandler);
      }
    }

    requestActivation() {
      if (window.flCanTrackMarketing === true) {
        this.activate(true);
        return;
      }
      var button = this.querySelector('.lite-yt-play');
      if (button) button.setAttribute('aria-label', this.playLabel + '. Marketing cookies are required.');
      if (typeof window.flOpenPrivacyChoices === 'function') {
        window.flOpenPrivacyChoices(button || this);
      }
    }

    setupPrivacyAwarePoster() {
      var self = this;
      this.posterConsentHandler = function() {
        if (window.flCanTrackMarketing === true) {
          var playButton = self.querySelector('.lite-yt-play');
          if (playButton) playButton.setAttribute('aria-label', self.playLabel);
          if (self.posterUrl) self.style.backgroundImage = 'url(' + self.posterUrl + ')';
          else self.setupPoster();
          if (self.autoload) self.activate(false);
        } else {
          if (self.classList.contains('is-activated')) self.deactivateForPrivacy();
          self.style.backgroundImage = '';
        }
      };
      document.addEventListener('fl:privacy-consent-change', this.posterConsentHandler);
      if (typeof window.flRunWhenPrivacyReady === 'function') {
        window.flRunWhenPrivacyReady(this.posterConsentHandler);
      } else {
        this.posterConsentHandler();
      }
    }

    disconnectedCallback() {
      if (this.posterConsentHandler) {
        document.removeEventListener('fl:privacy-consent-change', this.posterConsentHandler);
      }
      if (this.activationHandler) this.removeEventListener('click', this.activationHandler);
    }

    setupDOM() {
      this.style.backgroundSize = 'cover';
      this.style.backgroundPosition = 'center';
      this.style.cursor = 'pointer';
      this.style.position = 'relative';
      this.style.display = 'block';
      this.style.contain = 'content';
      this.style.aspectRatio = '16 / 9';
      this.style.width = '100%';

      // Play button
      var playBtn = document.createElement('button');
      playBtn.type = 'button';
      playBtn.className = 'lite-yt-play';
      playBtn.setAttribute('aria-label', this.playLabel);
      playBtn.innerHTML = '<svg width="68" height="48" viewBox="0 0 68 48"><path d="M66.52 7.74c-.78-2.93-2.49-5.41-5.42-6.19C55.79.13 34 0 34 0S12.21.13 6.9 1.55c-2.93.78-4.64 3.26-5.42 6.19C.06 13.05 0 24 0 24s.06 10.95 1.48 16.26c.78 2.93 2.49 5.41 5.42 6.19C12.21 47.87 34 48 34 48s21.79-.13 27.1-1.55c2.93-.78 4.64-3.26 5.42-6.19C67.94 34.95 68 24 68 24s-.06-10.95-1.48-16.26z" fill="red"/><path d="M45 24 27 14v20" fill="white"/></svg>';
      this.appendChild(playBtn);
    }

    setupPoster() {
      if (this.posterLoaded || this.posterRequested) return;
      this.posterRequested = true;
      // Use maxresdefault first, fallback to hqdefault
      var fallbackPoster = 'https://i.ytimg.com/vi/' + this.videoId + '/hqdefault.jpg';
      var poster = 'https://i.ytimg.com/vi/' + this.videoId + '/maxresdefault.jpg';

      var img = new Image();
      var self = this;
      img.onload = function() {
        // maxresdefault returns 120x90 placeholder if not available
        self.posterUrl = img.naturalWidth > 130 ? poster : fallbackPoster;
        if (window.flCanTrackMarketing === true) self.style.backgroundImage = 'url(' + self.posterUrl + ')';
        self.posterLoaded = true;
      };
      img.onerror = function() {
        self.posterUrl = fallbackPoster;
        if (window.flCanTrackMarketing === true) self.style.backgroundImage = 'url(' + fallbackPoster + ')';
      };
      img.src = poster;
    }

    activate(moveFocus) {
      if (window.flCanTrackMarketing !== true) return;
      if (this.classList.contains('is-activated')) return;
      var iframe = document.createElement('iframe');
      var params = this.params ? '&' + this.params : '';
      iframe.src = 'https://www.youtube-nocookie.com/embed/' + this.videoId + '?autoplay=1' + params;
      iframe.title = this.playLabel;
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
      iframe.allowFullscreen = true;
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      iframe.tabIndex = 0;
      iframe.style.border = '0';
      iframe.style.position = 'absolute';
      iframe.style.top = '0';
      iframe.style.left = '0';
      iframe.style.width = '100%';
      iframe.style.height = '100%';
      this.style.position = 'relative';
      this.innerHTML = '';
      this.appendChild(iframe);
      this.classList.add('is-activated');
      if (moveFocus) iframe.focus({ preventScroll: true });
    }

    deactivateForPrivacy() {
      var iframe = this.querySelector('iframe');
      var hadFocus = iframe && document.activeElement === iframe;
      this.classList.remove('is-activated');
      this.replaceChildren();
      this.setupDOM();
      this.style.backgroundImage = '';
      if (hadFocus) {
        var button = this.querySelector('.lite-yt-play');
        if (button) button.focus({ preventScroll: true });
      }
    }
  }

  // Register custom element
  if ('customElements' in window && !customElements.get('lite-youtube')) {
    customElements.define('lite-youtube', LiteYouTube);
  }
})();
